import { useState, useCallback, useMemo } from "react";
import { Link, useParams, useLocation } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import {
  Archive,
  ArchiveRestore,
  Briefcase,
  Building2,
  Calendar,
  Check,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  Star,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { Avatar, Badge, PageHeader, TagEditor } from "../../components/ui";
import type { TagItem } from "../../components/ui";
import { useToastPush } from "../../components/ui/Toast";
import { useAuth } from "../../auth/AuthProvider";
import { ManualSessionModal } from "../sessions/ManualSessionModal";
import { ClientPickerModal } from "./ClientPickerModal";
import {
  GET_PERSON_DETAIL,
  GET_CLIENT_NOTES,
  GET_ALL_PERSON_TAGS,
  UPDATE_PERSON,
  ADD_PERSON_TO_CLIENT,
  REMOVE_PERSON_FROM_CLIENT,
  SET_PRIMARY_PERSON,
  CREATE_PERSON_NOTE,
  UPDATE_PERSON_NOTE,
  DELETE_PERSON_NOTE,
} from "../../apollo/clients";
import {
  deriveStatus,
  getDisplayName,
  getSecondaryName,
  getInitials,
  formatSessionLabel,
  formatCurrency,
  CLIENT_STATUS_LABEL,
  CLIENT_STATUS_COLOR,
} from "../../data/clientDerive";
import type { DetailPerson, ClientNote } from "../../data/clientDerive";
import { timeAgo } from "../../utils/sessionTime";

const NOTE_MAX_CHARS = 5000;

/**
 * Person profile (/clients/people/:id): the contact-level identity. Manages
 * the person's own fields, tags, notes, and their Client memberships — a
 * person can belong to several Clients (1:1 is the common case), with one
 * primary Client that new bookings attribute to.
 */
export function PersonProfile() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const toast = useToastPush();
  const { user } = useAuth();

  const fromList = (location.state as { from?: string } | null)?.from;
  const backTo = fromList ? `/clients/people?${fromList}` : "/clients/people";

  const [isEditing, setIsEditing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [addToClientOpen, setAddToClientOpen] = useState(false);
  const [removeClientId, setRemoveClientId] = useState<string | null>(null);

  const [editName, setEditName] = useState("");
  const [editAlias, setEditAlias] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editBusiness, setEditBusiness] = useState("");
  const [editTitle, setEditTitle] = useState("");

  const [newNoteBody, setNewNoteBody] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteBody, setEditingNoteBody] = useState("");
  const [deleteConfirmNoteId, setDeleteConfirmNoteId] = useState<string | null>(null);

  // ── Queries ──────────────────────────────────────────────────────

  const { data, loading, error, refetch } = useQuery(GET_PERSON_DETAIL, {
    variables: { id },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  const person: DetailPerson | null = data?.Person ?? null;

  const notesVariables = useMemo(
    () => ({ page: 0, perPage: 200, filter: { personId: id } }),
    [id],
  );
  const { data: notesData } = useQuery(GET_CLIENT_NOTES, {
    variables: notesVariables,
    skip: !id,
    fetchPolicy: "cache-and-network",
  });
  const notes: ClientNote[] = notesData?.allPersonNotes ?? [];

  const { data: tagsVocabData } = useQuery(GET_ALL_PERSON_TAGS, {
    fetchPolicy: "cache-and-network",
  });
  const tagVocabulary: TagItem[] = tagsVocabData?.allPersonTags ?? [];

  // ── Mutations ────────────────────────────────────────────────────

  const detailRefetch = useMemo(
    () => [
      { query: GET_PERSON_DETAIL, variables: { id } },
      { query: GET_ALL_PERSON_TAGS },
    ],
    [id],
  );

  const [updatePerson, { loading: saving }] = useMutation(UPDATE_PERSON, {
    refetchQueries: detailRefetch,
  });
  const [addToClientMut, { loading: attaching }] = useMutation(ADD_PERSON_TO_CLIENT, {
    refetchQueries: detailRefetch,
  });
  const [removeFromClientMut, { loading: removing }] = useMutation(REMOVE_PERSON_FROM_CLIENT, {
    refetchQueries: detailRefetch,
  });
  const [setPrimaryMut] = useMutation(SET_PRIMARY_PERSON, {
    refetchQueries: detailRefetch,
  });

  const noteRefetch = useMemo(
    () => [{ query: GET_CLIENT_NOTES, variables: notesVariables }],
    [notesVariables],
  );
  const [createNote, { loading: creatingNote }] = useMutation(CREATE_PERSON_NOTE, {
    refetchQueries: noteRefetch,
  });
  const [updateNote] = useMutation(UPDATE_PERSON_NOTE, { refetchQueries: noteRefetch });
  const [deleteNote] = useMutation(DELETE_PERSON_NOTE, { refetchQueries: noteRefetch });

  // ── Handlers ─────────────────────────────────────────────────────

  const enterEditMode = useCallback(() => {
    if (!person) return;
    setEditName(person.name ?? "");
    setEditAlias(person.alias ?? "");
    setEditEmail(person.email);
    setEditPhone(person.phoneNumber ?? "");
    setEditBusiness(person.businessName ?? "");
    setEditTitle(person.title ?? "");
    setIsEditing(true);
  }, [person]);

  const saveEdit = useCallback(async () => {
    if (!editName.trim() || !editEmail.trim()) {
      toast({ kind: "error", title: "Name and email are required." });
      return;
    }
    try {
      await updatePerson({
        variables: {
          id,
          name: editName.trim(),
          alias: editAlias.trim() || null,
          email: editEmail.trim(),
          phoneNumber: editPhone.trim() || null,
          businessName: editBusiness.trim() || null,
          title: editTitle.trim() || null,
        },
      });
      setIsEditing(false);
      toast({ kind: "success", title: "Person updated" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to save", description: (err as Error).message });
    }
  }, [id, editName, editAlias, editEmail, editPhone, editBusiness, editTitle, updatePerson, toast]);

  const handleArchiveToggle = useCallback(async () => {
    if (!person) return;
    const archiving = !person.archivedAt;
    try {
      await updatePerson({
        variables: { id, archivedAt: archiving ? new Date().toISOString() : null },
      });
      toast({ kind: "success", title: archiving ? "Person archived" : "Person restored" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to update", description: (err as Error).message });
    }
  }, [id, person, updatePerson, toast]);

  const handleTagsChange = useCallback(
    (tagNames: string[]) => {
      updatePerson({ variables: { id, tagNames } }).catch((err) => {
        toast({ kind: "error", title: "Couldn't update tags", description: (err as Error).message });
      });
    },
    [id, updatePerson, toast],
  );

  const handleAddToClient = useCallback(
    async (clientId: string, clientLabel: string) => {
      try {
        await addToClientMut({ variables: { personId: id, clientId } });
        setAddToClientOpen(false);
        toast({ kind: "success", title: `Added to ${clientLabel}` });
      } catch (err) {
        toast({ kind: "error", title: "Failed to add to client", description: (err as Error).message });
      }
    },
    [id, addToClientMut, toast],
  );

  const handleRemoveMembership = useCallback(async () => {
    if (!removeClientId) return;
    try {
      await removeFromClientMut({ variables: { personId: id, clientId: removeClientId } });
      setRemoveClientId(null);
      toast({ kind: "success", title: "Removed from client" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to remove", description: (err as Error).message });
    }
  }, [id, removeClientId, removeFromClientMut, toast]);

  const handleSetPrimary = useCallback(
    async (clientId: string) => {
      try {
        await setPrimaryMut({ variables: { personId: id, clientId } });
        toast({ kind: "success", title: "Primary contact updated" });
      } catch (err) {
        toast({ kind: "error", title: "Failed to set primary", description: (err as Error).message });
      }
    },
    [id, setPrimaryMut, toast],
  );

  const handleAddNote = useCallback(async () => {
    if (!newNoteBody.trim()) return;
    try {
      await createNote({ variables: { personId: id, body: newNoteBody.trim() } });
      setNewNoteBody("");
      toast({ kind: "success", title: "Note added" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to add note", description: (err as Error).message });
    }
  }, [id, newNoteBody, createNote, toast]);

  const handleUpdateNote = useCallback(async (noteId: string) => {
    if (!editingNoteBody.trim()) return;
    try {
      await updateNote({ variables: { id: noteId, body: editingNoteBody.trim() } });
      setEditingNoteId(null);
      toast({ kind: "success", title: "Note updated" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to update note", description: (err as Error).message });
    }
  }, [editingNoteBody, updateNote, toast]);

  const handleDeleteNote = useCallback(async (noteId: string) => {
    try {
      await deleteNote({ variables: { id: noteId } });
      setDeleteConfirmNoteId(null);
      toast({ kind: "success", title: "Note deleted" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to delete note", description: (err as Error).message });
    }
  }, [deleteNote, toast]);

  // ── Derived ──────────────────────────────────────────────────────

  const memberships = person?.clients ?? [];
  const removingMembership = memberships.find((m) => m.id === removeClientId) ?? null;
  const sessions = (person?.bookings ?? []).filter((b) => b.deletedAt == null);

  // ── Render ───────────────────────────────────────────────────────

  if (loading && !person) {
    return (
      <div className="px-4 md:px-7 pt-6 pb-20 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-24 bg-canvas rounded" />
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-md bg-canvas" />
            <div className="space-y-2">
              <div className="h-6 w-48 bg-canvas rounded" />
              <div className="h-4 w-32 bg-canvas rounded" />
            </div>
          </div>
          <div className="h-32 bg-canvas rounded-lg" />
          <div className="h-64 bg-canvas rounded-lg" />
        </div>
      </div>
    );
  }

  if ((error && !person) || !person) {
    return (
      <div className="pb-20">
        <PageHeader heading={false} back={{ to: backTo, label: "people" }} />
        <div className="mx-auto mt-2 max-w-4xl bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <Users size={24} strokeWidth={1.5} className="mx-auto text-ink-4 mb-2" />
          <p className="text-ink-2 font-medium">
            {error ? "Failed to load person" : "Person not found"}
          </p>
          <p className="text-ink-4 text-[13px] mt-1">
            {error ? "Something went wrong. Please try again." : "This person may have been removed."}
          </p>
        </div>
      </div>
    );
  }

  const status = deriveStatus(person);
  const statusCfg = CLIENT_STATUS_COLOR[status];

  return (
    <div className="pb-20">
      <PageHeader heading={false} back={{ to: backTo, label: "people" }} />

      <div className="px-4 md:px-7 max-w-4xl mx-auto">

      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden mb-4">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar initials={getInitials(person)} size="lg" className="!w-16 !h-16 !text-[20px] !rounded-md" />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="font-display text-[26px] font-medium leading-tight">
                    {getDisplayName(person)}
                  </h1>
                  {person.archivedAt && <Badge variant="default">Archived</Badge>}
                </div>
                {getSecondaryName(person) && (
                  <div className="text-[13px] text-ink-4 mb-0.5">{getSecondaryName(person)}</div>
                )}
                <div className="flex items-center gap-2 text-[12.5px]">
                  <div className={`inline-flex items-center gap-1.5 font-medium ${statusCfg.text}`}>
                    <div className={`dot ${statusCfg.dot}`} />
                    {CLIENT_STATUS_LABEL[status]}
                  </div>
                  <span className="text-ink-5">·</span>
                  <span className="text-ink-4">
                    {person.sessionsCount} {person.sessionsCount === 1 ? "session" : "sessions"}
                  </span>
                  <span className="text-ink-5">·</span>
                  <span className="text-ink-4">{formatCurrency(person.lifetimeSpend)} lifetime</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setCreateOpen(true)}
                    className="px-3 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-hi flex items-center gap-1.5"
                  >
                    <Plus size={12} strokeWidth={2} />
                    New session
                  </button>
                  <a
                    href={`mailto:${person.email}`}
                    className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
                  >
                    <Mail size={12} strokeWidth={2} />
                    Email
                  </a>
                  {person.phoneNumber && (
                    <a
                      href={`tel:${person.phoneNumber}`}
                      className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
                    >
                      <Phone size={12} strokeWidth={2} />
                      Call
                    </a>
                  )}
                  <button
                    onClick={enterEditMode}
                    className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
                  >
                    <Pencil size={12} strokeWidth={2} />
                    Edit
                  </button>
                  <button
                    onClick={handleArchiveToggle}
                    className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-4 hover:bg-paper-2 flex items-center gap-1.5"
                  >
                    {person.archivedAt ? (
                      <ArchiveRestore size={12} strokeWidth={2} />
                    ) : (
                      <Archive size={12} strokeWidth={2} />
                    )}
                    {person.archivedAt ? "Restore" : "Archive"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-3 hover:bg-paper-2 flex items-center gap-1.5"
                  >
                    <X size={12} strokeWidth={2} />
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="px-3 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium flex items-center gap-1.5 hover:bg-tide-hi disabled:opacity-60"
                  >
                    <Check size={12} strokeWidth={2} />
                    {saving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Contact fields */}
        <div className="px-6 pb-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          {isEditing ? (
            <>
              {[
                { label: "Name", value: editName, set: setEditName, required: true },
                { label: "Alias", value: editAlias, set: setEditAlias },
                { label: "Email", value: editEmail, set: setEditEmail, required: true },
                { label: "Phone", value: editPhone, set: setEditPhone },
                { label: "Business name", value: editBusiness, set: setEditBusiness },
                { label: "Title", value: editTitle, set: setEditTitle },
              ].map((f) => (
                <div key={f.label}>
                  <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                    {f.label} {f.required && <span className="text-rust">*</span>}
                  </label>
                  <input
                    value={f.value}
                    onChange={(e) => f.set(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide"
                  />
                </div>
              ))}
            </>
          ) : (
            <>
              <div>
                <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">Email</dt>
                <dd className="text-[13px] text-ink-2 truncate">
                  <a href={`mailto:${person.email}`} className="hover:text-tide">{person.email}</a>
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">Phone</dt>
                <dd className="text-[13px] text-ink-2">
                  {person.phoneNumber ?? <span className="text-ink-5">--</span>}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">Business</dt>
                <dd className="text-[13px] text-ink-2 truncate">
                  {person.businessName ? (
                    <span className="flex items-center gap-1">
                      <Building2 size={12} strokeWidth={2} className="text-ink-4" />
                      {person.businessName}
                    </span>
                  ) : (
                    <span className="text-ink-5">--</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">Title</dt>
                <dd className="text-[13px] text-ink-2 truncate">
                  {person.title ? (
                    <span className="flex items-center gap-1">
                      <Briefcase size={12} strokeWidth={2} className="text-ink-4" />
                      {person.title}
                    </span>
                  ) : (
                    <span className="text-ink-5">--</span>
                  )}
                </dd>
              </div>
            </>
          )}
        </div>

        {/* Tags */}
        <div className="hair-t px-6 py-3">
          <div className="flex items-start gap-3">
            <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium pt-2 flex-shrink-0">
              Tags
            </dt>
            <dd className="flex-1 min-w-0">
              <TagEditor
                value={person.tags ?? []}
                vocabulary={tagVocabulary}
                onChange={handleTagsChange}
              />
            </dd>
          </div>
        </div>
      </div>

      {/* ── Memberships ───────────────────────────────────────────── */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden mb-4">
        <div className="px-6 pt-5 pb-4 flex items-center justify-between">
          <h2 className="text-[14px] font-semibold">
            {memberships.length === 1 ? "Client" : `Clients (${memberships.length})`}
          </h2>
          <button
            onClick={() => setAddToClientOpen(true)}
            className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
          >
            <UserPlus size={12} strokeWidth={2} />
            Add to client
          </button>
        </div>
        <div className="px-6 pb-6 space-y-2">
          {memberships.length === 0 ? (
            <div className="text-[13px] text-ink-4">No client memberships.</div>
          ) : (
            memberships.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 rounded-lg hair border px-4 py-3">
                <div className="min-w-0 flex items-center gap-2">
                  <Link to={`/clients/${m.id}`} className="text-[13.5px] font-medium hover:text-tide truncate">
                    {m.alias || m.name}
                  </Link>
                  {m.isPrimary && <Badge variant="spark">Primary contact</Badge>}
                  {m.status === "archived" && <Badge variant="default">Archived</Badge>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {!m.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(m.id)}
                      className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-3 hover:bg-paper-2 flex items-center gap-1"
                      title="Make this person that client's primary contact"
                    >
                      <Star size={11} strokeWidth={2} />
                      Make primary
                    </button>
                  )}
                  {memberships.length > 1 && (
                    <button
                      onClick={() => setRemoveClientId(m.id)}
                      className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-4 hover:bg-paper-2 hover:text-rust flex items-center gap-1"
                    >
                      <X size={11} strokeWidth={2} />
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Sessions ──────────────────────────────────────────────── */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden mb-4">
        <div className="px-6 pt-5 pb-4">
          <h2 className="text-[14px] font-semibold">
            Sessions {sessions.length > 0 && `(${sessions.length})`}
          </h2>
        </div>
        <div className="px-6 pb-6">
          {sessions.length === 0 ? (
            <div className="text-[13px] text-ink-4">No sessions yet.</div>
          ) : (
            <div className="space-y-1">
              {sessions.slice(0, 20).map((b) => (
                <Link
                  key={b.id}
                  to={`/sessions/${b.id}`}
                  className="flex items-center justify-between gap-3 rounded-md hover:bg-canvas transition px-3 py-2.5"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Calendar size={14} strokeWidth={2} className="text-tide flex-shrink-0" />
                    <span className="text-[13px] text-ink-2 truncate">
                      {b.bookingType?.name ?? "Session"}
                    </span>
                    <span className="text-[11.5px] text-ink-5">{b.state}</span>
                  </div>
                  <span className="text-[12px] text-ink-4 flex-shrink-0">
                    {b.availability
                      ? formatSessionLabel(new Date(`${b.availability.date}T${b.availability.startTime}`))
                      : timeAgo(b.createdAt)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Notes ─────────────────────────────────────────────────── */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-6 pt-5 pb-4">
          <h2 className="text-[14px] font-semibold">Notes {notes.length > 0 && `(${notes.length})`}</h2>
        </div>
        <div className="px-6 pb-4">
          <div className="rounded-lg bg-canvas p-4">
            <textarea
              value={newNoteBody}
              onChange={(e) => setNewNoteBody(e.target.value)}
              placeholder="Add a note about this person..."
              maxLength={NOTE_MAX_CHARS}
              rows={2}
              className="w-full px-3 py-2 text-[13px] rounded-md bg-paper-hi hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5 resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-ink-5">
                {newNoteBody.length > 0 && `${newNoteBody.length} / ${NOTE_MAX_CHARS}`}
              </span>
              <button
                onClick={handleAddNote}
                disabled={!newNoteBody.trim() || creatingNote}
                className="px-3 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium flex items-center gap-1.5 hover:bg-tide-hi disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={12} strokeWidth={2} />
                {creatingNote ? "Adding..." : "Add note"}
              </button>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 space-y-1">
          {notes.map((n) => {
            const isOwn = n.authorId === user?.id;
            const isEditingThis = editingNoteId === n.id;
            const isDeleteConfirm = deleteConfirmNoteId === n.id;
            return (
              <div key={n.id} className="group rounded-md hover:bg-canvas transition px-3 py-2.5">
                <div className="flex items-start gap-3">
                  <MessageSquare size={16} strokeWidth={2} className="text-spark mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12.5px] font-medium text-ink-3">{n.authorEmail}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isOwn && !isEditingThis && !isDeleteConfirm && (
                          <div className="hidden group-hover:flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingNoteId(n.id);
                                setEditingNoteBody(n.body);
                              }}
                              className="p-1 rounded hover:bg-paper-2 text-ink-4 hover:text-ink-2"
                              aria-label="Edit note"
                            >
                              <Pencil size={12} strokeWidth={2} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmNoteId(n.id)}
                              className="p-1 rounded hover:bg-paper-2 text-ink-4 hover:text-rust"
                              aria-label="Delete note"
                            >
                              <Trash2 size={12} strokeWidth={2} />
                            </button>
                          </div>
                        )}
                        <span className="text-[11px] text-ink-5">{timeAgo(n.createdAt)}</span>
                      </div>
                    </div>
                    {isEditingThis ? (
                      <div className="mt-2">
                        <textarea
                          value={editingNoteBody}
                          onChange={(e) => setEditingNoteBody(e.target.value)}
                          maxLength={NOTE_MAX_CHARS}
                          rows={3}
                          className="w-full px-3 py-2 text-[13px] rounded-md bg-paper-hi hair border focus:outline-none focus:border-tide text-ink resize-none"
                        />
                        <div className="flex items-center justify-end gap-2 mt-2">
                          <button
                            onClick={() => setEditingNoteId(null)}
                            className="px-2.5 py-1 rounded text-[12px] text-ink-3 hover:bg-paper-2"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateNote(n.id)}
                            disabled={!editingNoteBody.trim()}
                            className="px-2.5 py-1 rounded bg-tide text-paper-hi text-[12px] font-medium hover:bg-tide-hi disabled:opacity-50"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : isDeleteConfirm ? (
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[12.5px] text-ink-3">Delete this note?</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setDeleteConfirmNoteId(null)}
                            className="px-2.5 py-1 rounded text-[12px] text-ink-3 hover:bg-paper-2"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleDeleteNote(n.id)}
                            className="px-2.5 py-1 rounded bg-rust text-paper-hi text-[12px] font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[13px] text-ink-2 whitespace-pre-wrap break-words mt-1">
                        {n.body}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {notes.length === 0 && (
            <div className="text-[12.5px] text-ink-4 px-3 pb-2">No notes yet.</div>
          )}
        </div>
      </div>

      {/* ── Remove membership confirmation ────────────────────────── */}
      {removingMembership && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setRemoveClientId(null)} />
          <div className="relative bg-paper-hi rounded-lg shadow-lift w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-[16px] font-medium mb-2">
              Remove {getDisplayName(person)} from {removingMembership.alias || removingMembership.name}?
            </h3>
            <p className="text-[13px] text-ink-3 mb-5">
              Their other memberships remain, and past sessions and payments
              stay attributed to that client.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setRemoveClientId(null)}
                className="px-4 py-2 rounded-md hair border text-[13px] text-ink-2 hover:bg-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveMembership}
                disabled={removing}
                className="px-4 py-2 rounded-md bg-rust text-paper-hi text-[13px] font-medium disabled:opacity-60"
              >
                {removing ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add-to-client picker ──────────────────────────────────── */}
      <ClientPickerModal
        open={addToClientOpen}
        title={`Add ${getDisplayName(person)} to...`}
        confirmLabel="Add to client"
        confirmNote={(target) =>
          `${getDisplayName(person)} becomes a member of ${target} as well. Their primary client is unchanged.`
        }
        busy={attaching}
        onClose={() => setAddToClientOpen(false)}
        onConfirm={handleAddToClient}
      />

      <ManualSessionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        presetCustomerId={id}
        onCreated={() => refetch()}
      />
      </div>
    </div>
  );
}
